import { Link, useParams } from "react-router-dom";
import "./../styles/product-reviews.css";
import { FaStar } from "react-icons/fa";
import { useEffect, useMemo, useState } from "react";
import StarRating from "../components/star-rating/star-rating";
import RankingIcon from "../components/ranking/ranking";
import { showToast } from "../components/toasts/ToastNotification";
import { getProductReviewsBySlug } from "../api/review.api";
import { HashLoader } from "react-spinners";
import { userRankText } from "../utils";
import { FiMessageSquare } from "react-icons/fi";
import { FaAngleLeft } from "react-icons/fa6";

export const ProductReviews = () => {
  const { product_slug } = useParams();
  const [selected, setSelected] = useState("all");
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    fetchProductReviews();
    document.title = "Đánh giá sản phẩm";
  }, [product_slug]);

  const fetchProductReviews = async () => {
    setLoading(true);
    try {
      const res = await getProductReviewsBySlug(product_slug, 50);
      if (res.success) {
        setReviews(res.data.product_reviews || null);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const allReviewItems = reviews?.reviews || [];
  const filteredReviews = selected === "all" ? allReviewItems : allReviewItems.filter((rv) => rv.rating === parseInt(selected, 10));

  const summary = useMemo(() => {
    const total = allReviewItems.length;
    const avgRating =
      total > 0 ? allReviewItems.reduce((sum, item) => sum + Number(item?.rating || 0), 0) / total : 0;

    const starBuckets = allReviewItems.reduce(
      (acc, item) => {
        const star = Number(item?.rating || 0);
        if (star >= 1 && star <= 5) acc[star] += 1;
        return acc;
      },
      { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    );

    return {
      total,
      avgRating,
      starBuckets,
    };
  }, [allReviewItems]);

  if (loading) {
    return (
      <div className="prx-page-loading loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  return (
    <section className="prx-page">
      <div className="prx-shell">
        <header className="prx-head">
          <div className="prx-head-left">
            <Link to={`/product/${product_slug}`} className="prx-back-link">
              <FaAngleLeft />
              Quay lại sản phẩm
            </Link>
            <h1>Đánh giá khách hàng</h1>
            <p className="prx-subtitle">Tổng hợp phản hồi từ người đã mua sản phẩm.</p>
          </div>

          <div className="prx-score-card">
            <strong>{summary.avgRating.toFixed(1)}</strong>
            <div className="prx-score-stars">
              <FaStar />
              <span>/ 5.0</span>
            </div>
            <p>{summary.total.toLocaleString("vi-VN")} đánh giá</p>
          </div>
        </header>

        <div className="prx-filters-wrap">
          <div className="prx-filters">
            <ReviewCategory number="all" setSelected={setSelected} selected={selected} count={summary.total} />
            <ReviewCategory number="5" setSelected={setSelected} selected={selected} count={summary.starBuckets[5]} />
            <ReviewCategory number="4" setSelected={setSelected} selected={selected} count={summary.starBuckets[4]} />
            <ReviewCategory number="3" setSelected={setSelected} selected={selected} count={summary.starBuckets[3]} />
            <ReviewCategory number="2" setSelected={setSelected} selected={selected} count={summary.starBuckets[2]} />
            <ReviewCategory number="1" setSelected={setSelected} selected={selected} count={summary.starBuckets[1]} />
          </div>
        </div>

        {filteredReviews.length > 0 ? (
          <div className="prx-review-grid">
            {filteredReviews.map((review) => (
              <ReviewItem key={review?._id} review={review} />
            ))}
          </div>
        ) : (
          <div className="prx-empty-state">
            <div className="prx-empty-icon-wrap">
              <FiMessageSquare className="prx-empty-icon" />
            </div>
            <p className="prx-empty-title">Chưa có đánh giá phù hợp</p>
            <p className="prx-empty-sub">Hãy thử đổi bộ lọc hoặc quay lại sau.</p>
          </div>
        )}
      </div>
    </section>
  );
};

const ReviewCategory = ({ number, setSelected, selected, count }) => {
  return (
    <button
      type="button"
      onClick={() => setSelected(number)}
      className={`prx-filter-chip ${selected === number ? "is-active" : ""}`}
    >
      {number !== "all" ? (
        <>
          <span>{number}</span>
          <FaStar className="prx-filter-star" />
        </>
      ) : (
        <span>Tất cả</span>
      )}
      <small>{count}</small>
    </button>
  );
};

const ReviewItem = ({ review }) => {
  return (
    <article className="prx-review-item">
      <img
        className="prx-review-avatar"
        src={review?.userId?.profileImg || "https://i.pravatar.cc/300"}
        alt="user-avatar"
      />
      <div className="prx-review-content">
        <div className="prx-review-top">
          <p className="prx-review-user">
            {review?.userId?.username || "Người dùng"}
          </p>
          <StarRating rating={review?.rating || 0} />
        </div>
        <div className="prx-review-rank">
          <RankingIcon rank={review?.userId?.rank || "diamond"} />
          <p className="prx-review-rank-text">
            {userRankText(review?.userId?.rank) || ""}
          </p>
        </div>
        <div className="prx-review-bottom">
          <p className="prx-review-comment">{review?.comment || "Không có nội dung bình luận."}</p>
          <p className="prx-review-date">
            {new Date(review?.createdAt).toLocaleDateString() || ""}
          </p>
        </div>
      </div>
    </article>
  );
};
